import * as React from 'react';
import type { Variant } from 'framer-motion';
import { motion } from 'framer-motion';
import Button, { HybridLinkButton } from '../Button/Button';
import { DEFAULT_TRANSITION } from '../../../styles/animations/framerGlobalConfig';
import type AppError from '../../../lib/AppError';
import useUpdateRef from '../../../hooks/useUpdateRef';
import ErrorDetailsView from '../ErrorDetailsView/ErrorDetailsView';
import cn from '~modules/cn';
import InfoIcon from '../../../assets/icons/v2design/basics/info.svg';
import CheckIcon from '../../../assets/icons/v2design/basics/check.svg';
import AlertIcon from '../../../assets/icons/v2design/basics/alert.svg';
import CloseIcon from '../../../assets/icons/v2design/basics/close.svg';

import './toast.scss';

export type ToastType = 'info' | 'success' | 'error' | 'general';

const ToastIcon: { [K in ToastType]: ReactSvgImage } = {
    info: InfoIcon,
    success: CheckIcon,
    error: AlertIcon,
    general: InfoIcon
};

export type ToastProps = {
    type: ToastType;
    label: string;
    icon?: ReactSvgImage | null;
    buttonTitle?: string;
    link?: string;
    onButtonClick?: { (props: ToastProps): $TSFixMe };
    onDismiss?: { (): void };
    disappear?: number;
    id?: string;
    children?: React.ReactNode;
    error?: AppError;
    className?: string;
    containerClassName?: string;
    showCloseButton?: boolean;
};

export type Props = ToastProps & {
    showAnimation?: Variant;
    hideAnimation?: Variant;
    animate?: boolean;
};

const ToastButton = ({ onClick, buttonTitle, to }: { buttonTitle: string; onClick?: () => void; to?: string }) => (
    <HybridLinkButton buttonType="secondary" buttonSize="small" onClick={onClick} to={to}>
        {buttonTitle}
    </HybridLinkButton>
);

const Toast = (props: Props): JSX.Element => {
    const { onDismiss, onButtonClick, disappear, showCloseButton = true, icon: Icon = ToastIcon[props.type] } = props;

    const dismissRef = useUpdateRef(onDismiss);
    const propsRef = useUpdateRef(props);
    const isHoverRef = React.useRef(false);
    const shouldDismissRef = React.useRef(false);

    React.useEffect(() => {
        let timeout: number | undefined;
        shouldDismissRef.current = false;
        if (disappear) {
            timeout = window.setTimeout(() => {
                if (isHoverRef.current) {
                    shouldDismissRef.current = true;
                } else {
                    dismissRef.current?.();
                }
            }, disappear);
        }
        return () => {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        };
    }, [disappear, dismissRef]);

    const buttonClickHandler = React.useCallback(() => {
        const res = onButtonClick?.(propsRef.current!);
        if (res === true) {
            dismissRef.current?.();
        }
    }, [dismissRef, onButtonClick, propsRef]);

    return (
        <motion.div
            className={cn(`toast toast--${props.type}`, props.className)}
            initial={props.animate ? 'hide' : 'show'}
            animate="show"
            exit="hide"
            transition={DEFAULT_TRANSITION}
            variants={{
                show: {
                    opacity: 1,
                    height: 'auto',
                    ...(props.showAnimation ?? {})
                },
                hide: {
                    opacity: 0,
                    height: 0,
                    ...(props.hideAnimation ?? {})
                }
            }}
            onMouseEnter={() => {
                isHoverRef.current = true;
            }}
            onMouseLeave={() => {
                if (shouldDismissRef.current) {
                    dismissRef.current?.();
                }
                isHoverRef.current = false;
            }}
        >
            <div className={cn('toast__container', props.containerClassName)}>
                {Icon && <Icon className="toast__icon" />}
                <div className="toast__label">{props.label}</div>
                {props.buttonTitle && <ToastButton buttonTitle={props.buttonTitle} to={props.link} onClick={buttonClickHandler} />}
                {showCloseButton && (
                    <Button className="toast__close-button" buttonSize="small" buttonType="transparent" icon={<CloseIcon />} onClick={onDismiss} />
                )}
            </div>
            {(props.children || props.error) && (
                <div className="toast__children">
                    {props.error && <ErrorDetailsView error={props.error} />}
                    {props.children}
                </div>
            )}
        </motion.div>
    );
};

export default Toast;
